import React from 'react';
import { useBranding } from '../../../hooks/useBranding';
import { useStoreContact } from './useStoreChrome';

/**
 * Scrolling utility bar shown above the main nav on every storefront template.
 * Repeats the store's banner text and surfaces quick contact links.
 */
const AnnouncementBar = ({
  bg = '#141414',
  fg = '#ffffff',
  border = '#ededed',
  fallbackText = 'Livraison rapide · Paiement à la livraison · Partout en Tunisie',
}) => {
  const { bannerText } = useBranding();
  const { phone, facebook, instagram, tiktok } = useStoreContact();
  const message = bannerText || fallbackText;
  const whatsappHref = phone ? `https://wa.me/${phone.replace(/[^\d]/g, '')}` : null;

  const hasSocial = facebook || instagram || tiktok || whatsappHref;

  return (
    <div
      className="flex items-center gap-3 overflow-hidden border-b px-3 py-1.5 text-[11px] font-semibold sm:text-xs"
      style={{ backgroundColor: bg, color: fg, borderBottomColor: border }}
    >
      {hasSocial && (
        <div className="hidden shrink-0 items-center gap-2.5 sm:flex">
          {tiktok && (
            <a href={tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="opacity-80 hover:opacity-100">
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M16.6 5.82c-.86-.95-1.34-2.18-1.34-3.47h-3.06v14.2a3.1 3.1 0 01-5.44 2.03 3.1 3.1 0 012.14-5.3c.3 0 .58.04.85.12V10.3a6.14 6.14 0 00-.85-.06A6.17 6.17 0 002 16.4a6.17 6.17 0 0010.26 4.63A6.11 6.11 0 0014.2 16.5V9.4a8.16 8.16 0 004.76 1.52V7.86c-.86 0-1.69-.26-2.36-.75-.73-.52-1.65-1.24-2-1.29z" />
              </svg>
            </a>
          )}
          {facebook && (
            <a href={facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="opacity-80 hover:opacity-100">
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22 12a10 10 0 10-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.78-3.89 1.1 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.44 2.89h-2.34v6.99A10 10 0 0022 12z" />
              </svg>
            </a>
          )}
          {instagram && (
            <a href={instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="opacity-80 hover:opacity-100">
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.2c3.2 0 3.58.01 4.85.07 1.17.05 1.97.24 2.43.4a4.4 4.4 0 011.6 1.04c.47.46.8.96 1.03 1.6.17.46.36 1.26.4 2.43.07 1.27.08 1.65.08 4.85s-.01 3.58-.08 4.85c-.04 1.17-.23 1.97-.4 2.43a4.4 4.4 0 01-1.03 1.6 4.4 4.4 0 01-1.6 1.03c-.46.17-1.26.36-2.43.4-1.27.07-1.65.08-4.85.08s-3.58-.01-4.85-.08c-1.17-.04-1.97-.23-2.43-.4a4.4 4.4 0 01-1.6-1.03 4.4 4.4 0 01-1.03-1.6c-.17-.46-.36-1.26-.4-2.43C1.62 15.58 1.6 15.2 1.6 12s.01-3.58.08-4.85c.04-1.17.23-1.97.4-2.43a4.4 4.4 0 011.03-1.6 4.4 4.4 0 011.6-1.03c.46-.17 1.26-.36 2.43-.4C8.42 2.2 8.8 2.2 12 2.2zm0 1.8c-3.14 0-3.5.01-4.73.07-1 .04-1.55.2-1.91.34-.48.18-.82.4-1.18.76-.36.36-.58.7-.76 1.18-.14.36-.3.9-.34 1.91C3.01 8.5 3 8.86 3 12s.01 3.5.08 4.74c.04 1 .2 1.55.34 1.91.18.48.4.82.76 1.18.36.36.7.58 1.18.76.36.14.9.3 1.91.34 1.23.06 1.59.07 4.73.07s3.5-.01 4.73-.07c1-.04 1.55-.2 1.91-.34.48-.18.82-.4 1.18-.76.36-.36.58-.7.76-1.18.14-.36.3-.9.34-1.91.06-1.23.07-1.59.07-4.74 0-3.14-.01-3.5-.07-4.73-.04-1-.2-1.55-.34-1.91a3.1 3.1 0 00-.76-1.18 3.1 3.1 0 00-1.18-.76c-.36-.14-.9-.3-1.91-.34C15.5 4.01 15.14 4 12 4zm0 3.6a4.4 4.4 0 110 8.8 4.4 4.4 0 010-8.8zm0 1.8a2.6 2.6 0 100 5.2 2.6 2.6 0 000-5.2zm4.6-2a1 1 0 110 2 1 1 0 010-2z" />
              </svg>
            </a>
          )}
          {whatsappHref && (
            <a href={whatsappHref} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="opacity-80 hover:opacity-100">
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12.04 2c-5.5 0-9.96 4.46-9.96 9.96 0 1.76.46 3.4 1.26 4.83L2 22l5.37-1.3a9.9 9.9 0 004.67 1.19h.01c5.5 0 9.96-4.46 9.96-9.96S17.54 2 12.04 2zm5.84 14.1c-.25.69-1.24 1.27-2.01 1.43-.53.11-1.23.2-3.58-.77-3-1.24-4.93-4.26-5.08-4.46-.15-.2-1.21-1.6-1.21-3.06 0-1.45.76-2.17 1.03-2.46.25-.28.57-.35.76-.35.19 0 .38 0 .55.01.17.01.42-.06.65.5.25.59.84 2.04.91 2.19.07.15.12.33.02.53-.1.2-.15.33-.3.5-.15.18-.31.4-.45.53-.15.15-.3.31-.13.6.17.3.76 1.26 1.64 2.04 1.12 1 2.07 1.31 2.37 1.46.3.15.47.13.65-.08.18-.2.76-.89.96-1.19.2-.3.4-.25.68-.15.28.1 1.76.83 2.06.98.3.15.5.23.57.35.08.13.08.72-.17 1.4z" />
              </svg>
            </a>
          )}
        </div>
      )}

      <div className="min-w-0 flex-1 overflow-hidden">
        <div className="inline-flex animate-marquee whitespace-nowrap">
          <span className="mx-8">{message}</span>
          <span className="mx-8">{message}</span>
          <span className="mx-8">{message}</span>
          <span className="mx-8">{message}</span>
        </div>
      </div>

      {phone && (
        <a href={`tel:${phone.replace(/\s/g, '')}`} className="flex shrink-0 items-center gap-1.5 font-semibold opacity-90 hover:opacity-100">
          <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.04 11.04 0 005.516 5.516l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
          </svg>
          <span className="hidden sm:inline">{phone}</span>
        </a>
      )}
    </div>
  );
};

export default AnnouncementBar;
